import { BillingDetails, StripeElements } from "@stripe/stripe-js";
import { Action, getModule, Module, Mutation, VuexModule } from "vuex-module-decorators";

import config from "@/config";
import log from "@/helpers/log";
import { BILLING_MODULE_KEY, STRIPE_INVOICE_STATUS, SUBSCRIPTION_PLAN } from "@/utils/enums";
import { Invoice, InvoiceLinks, RedeemCouponResult, Subscription, SubscriptionDowngrade, SubscriptionResponse } from "@/utils/interface";
import store from "@/vuexStore";

import { getStripeClient } from "../../../lib/stripeClient";
import { authGet, authPatch, authPost } from "../authStoreRequest";

// @ts-expect-error decorator is picking the wrong overload as its type
@Module({
  namespaced: true,
  name: BILLING_MODULE_KEY,
  store,
  dynamic: true,
})
class BillingModule extends VuexModule {
  invoices: { team: Record<number, Invoice[]> } = {
    team: {},
  };

  invoiceLinks: Record<number, Record<string, InvoiceLinks | undefined>> = {};

  mauUsage: { team: Record<number, { mau: number; sms: number }> } = {
    team: {},
  };

  subscription: { team: Record<number, Subscription> } = {
    team: {},
  };

  subscriptionDowngrade: { team: Record<string, SubscriptionDowngrade | null> } = {
    team: {},
  };

  public get invoicesArray() {
    return (teamId: number) => this.invoices?.team[teamId] || [];
  }

  public get getSubscriptionState() {
    return (teamId: number) => this.subscription?.team[teamId] || ({} as Subscription);
  }

  public get getSubscriptionLoaded() {
    return (teamId: number) => {
      return !!Object.keys(this.getSubscriptionState(teamId)).length;
    };
  }

  public get getSubscription() {
    return (teamId: number) => {
      const subscription = this.getSubscriptionState(teamId) ?? {};
      return { ...subscription } as Subscription;
    };
  }

  public get getSubscriptionDowngrade() {
    return (teamId: number): SubscriptionDowngrade | null => this.subscriptionDowngrade.team[teamId];
  }

  public get subscriptionPlan() {
    return (teamId: number) => {
      const subscription = this.getSubscription(teamId);
      return subscription?.customer?.plan ?? SUBSCRIPTION_PLAN.base;
    };
  }

  public get getMauUsage() {
    return (teamId: number) => this.mauUsage.team[teamId];
  }

  public get getPendingInvoices() {
    return (teamId: number) =>
      this.invoicesArray(teamId).filter(
        (invoice: Invoice) => invoice.status === STRIPE_INVOICE_STATUS.open && invoice.due_date && new Date(invoice.due_date) < new Date()
      ) ?? [];
  }

  public get hasPendingInvoices() {
    return (teamId: number) => this.invoicesArray(teamId).some((invoice: Invoice) => invoice.status === STRIPE_INVOICE_STATUS.open);
  }

  public get getInvoiceLinks() {
    return (teamId: number, invoiceId: string) => this.invoiceLinks[teamId]?.[invoiceId];
  }

  @Action
  async getInvoices({ teamId }: { teamId: number }) {
    try {
      const apiEndpoint = `${config.backendURL}/billing/teams/${teamId}/invoices`;
      const response = await authGet(apiEndpoint, {}, {});
      const { invoices } = response as { invoices: Invoice[] };
      this.setInvoices({ invoices, teamId });
    } catch (e) {
      log.error("Error fetching invoices", e);
      throw e;
    }
  }

  @Action
  async fetchInvoiceLinks({ teamId, invoiceId }: { teamId: number; invoiceId: string }) {
    try {
      const apiEndpoint = `${config.backendURL}/billing/teams/${teamId}/invoices/${invoiceId}/links`;
      const response = await authGet(apiEndpoint, {}, {});
      const links = response as InvoiceLinks;
      this.setInvoiceLinks({ teamId, invoiceId, invoiceLinks: links });
    } catch (e) {
      log.error("Error fetching invoice payment links", e);
      throw e;
    }
  }

  @Action
  async fetchMauUsage({ teamId }: { teamId: number }) {
    try {
      const apiEndpoint = `${config.backendURL}/billing/teams/${teamId}/data-usage`;
      const response = await authGet(apiEndpoint, {}, {});
      const { mau, sms } = response as { mau: number; sms: number };
      this.setMauUsage({ mau, sms, teamId });
    } catch (error) {
      log.error("Error getting MAU and SMS usage of mainnet verifiers and projects", error);
    }
  }

  @Action
  async fetchSubscription({ teamId }: { teamId: number }) {
    try {
      const apiEndpoint = `${config.backendURL}/billing/teams/${teamId}/subscription`;
      const response = await authGet(apiEndpoint, {}, {});
      const { subscription } = response as { subscription: Subscription };
      this.setSubscription({ payload: subscription, teamId });
    } catch (e) {
      log.error("Error fetching subscriptions", e);
      throw e;
    }
  }

  @Action
  async fetchSubscriptionDowngrade({ teamId }: { teamId: number }) {
    const apiEndpoint = `${config.backendURL}/billing/teams/${teamId}/subscription/downgrade`;
    const downgrade = (await authGet(apiEndpoint, {}, {})) as SubscriptionDowngrade | null;
    this.setSubscriptionDowngrade({ subscriptionDowngrade: downgrade, teamId });
  }

  @Action
  async redeemCoupon({ couponCode, teamId }: { couponCode: string; teamId: number }): Promise<RedeemCouponResult> {
    const apiEndpoint = `${config.backendURL}/billing/teams/${teamId}/redeem-coupon`;
    const response = await authPost(apiEndpoint, { coupon_code: couponCode }, {});
    await this.fetchSubscription({ teamId });
    return response as Promise<RedeemCouponResult>;
  }

  @Action
  async createSetupIntent({ teamId }: { teamId: number }) {
    const apiEndpoint = `${config.backendURL}/billing/teams/${teamId}/setup-stripe-intent`;
    const response = await authPost(apiEndpoint, {}, {});
    return response as { client_secret: string };
  }

  @Action
  async createSubscription({ teamId, subscriptionPlan, couponCode }: { teamId: number; subscriptionPlan: string; couponCode?: string }) {
    const apiEndpoint = `${config.backendURL}/billing/teams/${teamId}/subscriptions`;
    const body: { subscription_plan: string; coupon_code?: string } = {
      subscription_plan: subscriptionPlan,
      ...(couponCode ? { coupon_code: couponCode } : {}),
    };
    const response = await authPost(apiEndpoint, body, {});
    return response as SubscriptionResponse;
  }

  @Action
  async updateSubscription({ teamId, subscriptionPlan, couponCode }: { teamId: number; subscriptionPlan: string; couponCode?: string }) {
    const apiEndpoint = `${config.backendURL}/billing/teams/${teamId}/subscription`;
    const body: { subscription_plan: string; coupon_code?: string } = {
      subscription_plan: subscriptionPlan,
      ...(couponCode ? { coupon_code: couponCode } : {}),
    };
    const response = await authPatch(apiEndpoint, body, {});
    return response as SubscriptionResponse;
  }

  @Action
  async downgradeSubscription({ teamId, plan }: { teamId: number; plan: string }) {
    const apiEndpoint = `${config.backendURL}/billing/teams/${teamId}/subscription/downgrade`;
    const response = await authPost(apiEndpoint, { plan_type: plan }, {});
    await this.fetchSubscriptionDowngrade({ teamId });
    return response as SubscriptionResponse;
  }

  @Action
  async cancelDowngrade({ teamId, scheduleId }: { teamId: number; scheduleId: string }) {
    const apiEndpoint = `${config.backendURL}/billing/teams/${teamId}/subscription/downgrade/cancel`;
    const response = await authPost(apiEndpoint, { schedule_id: scheduleId }, {});
    await this.fetchSubscriptionDowngrade({ teamId });
    return response as SubscriptionResponse;
  }

  @Action
  async setDefaultPaymentMethod({ teamId, paymentMethodID }: { teamId: number; paymentMethodID: string }) {
    const apiEndpoint = `${config.backendURL}/billing/teams/${teamId}/payment-method`;
    return authPost(apiEndpoint, { payment_method_id: paymentMethodID }, {});
  }

  @Action
  async addCard({ stripeElements, teamId }: { stripeElements: StripeElements; teamId: number }) {
    const stripeClient = getStripeClient();
    const result = await stripeClient.confirmSetup({
      elements: stripeElements,
      redirect: "if_required",
    });
    if (result.error) throw result.error;

    const { setupIntent } = result;
    // set created paymentMethod as default
    await this.setDefaultPaymentMethod({ teamId, paymentMethodID: setupIntent.payment_method as string });
    this.fetchSubscription({ teamId });
  }

  @Action
  async updateAddress({ teamId, paymentMethodID, billingDetails }: { teamId: number; paymentMethodID: string; billingDetails: BillingDetails }) {
    const apiEndpoint = `${config.backendURL}/billing/teams/${teamId}/update-address`;
    return authPost(apiEndpoint, { paymentMethodID, billingDetails }, {});
  }

  @Mutation
  setInvoices({ invoices, teamId }: { invoices: Invoice[]; teamId: number }) {
    this.invoices.team = { ...(this.invoices?.team || {}), [teamId]: invoices };
  }

  @Mutation
  setMauUsage({ mau, sms, teamId }: { mau: number; sms: number; teamId: number }) {
    this.mauUsage.team = { ...(this.mauUsage?.team || {}), [teamId]: { sms, mau } };
  }

  @Mutation
  setSubscription({ payload, teamId }: { payload: Subscription; teamId: number }) {
    this.subscription.team = { ...(this.subscription?.team || {}), [teamId]: payload };
  }

  @Mutation
  setSubscriptionDowngrade({ subscriptionDowngrade, teamId }: { subscriptionDowngrade: SubscriptionDowngrade | null; teamId: number }) {
    this.subscriptionDowngrade.team = { ...this.subscriptionDowngrade.team, [teamId]: subscriptionDowngrade };
  }

  @Mutation
  setInvoiceLinks({ teamId, invoiceId, invoiceLinks }: { teamId: number; invoiceId: string; invoiceLinks: InvoiceLinks }) {
    this.invoiceLinks = { ...this.invoiceLinks, [teamId]: { ...this.invoiceLinks[teamId], [invoiceId]: invoiceLinks } };
  }
}

const billingModule = getModule(BillingModule);

export default billingModule;
